import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ThomVerifiedStamp from "@/components/ThomVerifiedStamp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, ScanLine } from "lucide-react";

export default function VerifyCertificate() {
  const { certNumber } = useParams();
  const [input, setInput] = useState(certNumber || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!certNumber);
  const navigate = useNavigate();
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (!certNumber) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/certificates/${certNumber}`);
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, [certNumber]);

  const submit = (e) => {
    e.preventDefault();
    if (input.trim()) navigate(`/verify/${input.trim()}`);
  };

  const StatusPill = ({ status }) => {
    const map = {
      valid: "bg-primary text-primary-foreground",
      expired: "bg-amber-500 text-white",
      superseded: "bg-slate-500 text-white",
      revoked: "bg-red-700 text-white",
      suspended: "bg-red-500 text-white",
    };
    return <span className={`mono text-[11px] uppercase tracking-widest px-3 py-1 rounded-sm ${map[status] || "bg-secondary"}`}>{status}</span>;
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-center">
          <div className="kicker">Public Verification</div>
          <h1 className="display text-4xl mt-1">Verify a THOM Verify™ Certificate</h1>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto mt-3">
            Enter the certificate number printed on the report or the QR-linked page. This verification is public and shows only non-sensitive information.
          </p>
        </div>

        <form onSubmit={submit} className="flex gap-2 max-w-lg mx-auto mt-8">
          <Input placeholder="TVC-2026-XXXX-XXXX" value={input} onChange={(e) => setInput(e.target.value)} className="mono" data-testid="verify-input" />
          <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="verify-submit"><ScanLine className="h-4 w-4 mr-1" /> Verify</Button>
        </form>

        {loading && <div className="text-center text-muted-foreground mt-10">Checking…</div>}

        {data && !loading && (
          data.found ? (
            <div className="mt-10 border border-border p-6 bg-card" data-testid="verify-result">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-5 w-5 text-primary" />
                    <StatusPill status={data.status} />
                  </div>
                  <div className="display text-2xl">{data.asset.summary}</div>
                  <div className="mono text-xs text-muted-foreground">Certificate {data.certificate_number} · v{data.version} · template v{data.template_version}</div>
                  {data.asset.city && <div className="text-sm text-muted-foreground mt-1">Location: {data.asset.city}</div>}
                </div>
                <ThomVerifiedStamp
                  size={140}
                  verificationStatus={data.status === "valid" ? "verified" : data.status}
                  certificateId={data.certificate_number}
                  trustScore={data.trust_score}
                  verificationDate={data.issued_at}
                  expiryDate={data.expires_at}
                  showTooltip={false}
                />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div><div className="kicker">Trust Score</div><div className="display text-3xl text-primary">{data.trust_score}</div></div>
                <div><div className="kicker">Issued</div><div>{new Date(data.issued_at).toLocaleDateString()}</div></div>
                {data.expires_at && <div><div className="kicker">Expires</div><div>{new Date(data.expires_at).toLocaleDateString()}</div></div>}
                <div><div className="kicker">Document ID</div><div className="mono text-xs">{data.document_id}</div></div>
              </div>

              <div className="mt-4 text-xs mono text-muted-foreground break-all">Hash: {data.content_hash}</div>

              {data.status === "revoked" && (
                <div className="mt-4 p-3 border border-red-500 bg-red-50 text-red-800 text-sm">
                  Revoked on {new Date(data.revoked_at).toLocaleDateString()} — reason: {data.revocation_reason}
                </div>
              )}
              {data.superseded_by && (
                <div className="mt-4 p-3 border border-amber-500 bg-amber-50 text-amber-800 text-sm">
                  Superseded by <Link className="underline" to={`/verify/${data.superseded_by}`}>{data.superseded_by}</Link>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                <a href={`${API_BASE}${data.report_url}`} target="_blank" rel="noreferrer" className="btn-outline">View HTML Report</a>
                <Link to={`/vehicles/${data.asset.id}`} className="btn-outline">Open Listing</Link>
                <img src={`${API_BASE}/certificates/${data.certificate_number}/qr.png`} alt="QR" className="ml-auto h-24 w-24 border border-border" />
              </div>
            </div>
          ) : (
            <div className="mt-10 border border-red-500 p-6 bg-red-50" data-testid="verify-not-found">
              <div className="flex items-center gap-2 text-red-800"><ShieldAlert className="h-5 w-5" /> Not authentic</div>
              <p className="mt-2 text-sm text-red-800">{data.message}</p>
              <p className="mt-1 text-xs mono text-red-800">Certificate: {data.certificate_number}</p>
            </div>
          )
        )}
      </div>
      <Footer />
    </div>
  );
}
